import TopFilterBase from "app/commonComponents/listBase/TopFilterBase";
import { CheckOutlined } from "@ant-design/icons";
import Buttons from "base/basicComponents/button";
import ReadOnlyText from "base/basicComponents/readOnly-text";
import { Divider, message, Tooltip } from "antd";
import { Select as AntdV5Select, ConfigProvider as ConfigProvider5 } from "antd-v5";
import type { SelectProps as AntdV5SelectProps } from "antd-v5";
import { Fragment, useEffect, useMemo, useRef } from "react";
import { useClickAway, useRequest, useSetState } from "ahooks";
import { wrapRequestToPromise } from "utils/index";
import ProcedureDetailService from "service/procedureFile/ProcedureDetailService";
import "./index.scss";
import { GetProcedureListNewResponseType } from "service/procedureList/types";

enum AssignPPOSelectStatus {
  Success,
  Conflict,
}

enum HoverMessageType {
  "There is a pending Change Primary Procedure Owner process. Please go to To Do List to assign Primary Procedure Owner." = 1,
  "Please go to Procedure Detail Page -> Procedure Information section to change Primary Procedure Owner." = 2,
  "Primary Procedure Owner cannot be changed while a Manage Shared Task or Change Main Task process is pending. Please complete the pending process and assign the Primary Procedure Owner from the To Do List." = 3,
}

const { sortListV5, handleFilterOptionV5 } = TopFilterBase();

interface AssignPPOSelectProps extends AntdV5SelectProps {
  initDisabled: boolean;
  hoverMessage: 0 | 1 | 2;
  procedureTaskId: string;
  handleSubmitPPO: (
    para: {
      primaryProcedureOwnerId: string;
      procedureTaskId: string;
    },
    submitSuccess: (
      res:
        | { data: { procedureID: string; primaryProcedureOwnerID: string } }
        | AssignPPOSelectStatus.Conflict
    ) => void,
    submitFaild: (error: any) => void
  ) => any;
  setProcedureListData: (list: GetProcedureListNewResponseType["newData"]) => void;
  procedureListData: GetProcedureListNewResponseType["newData"];
  primaryProcedureOwnerName: string;
}

interface AssignPPOSelectState {
  disabled: boolean;
  selectValue: string;
  options: { label: string; value: string }[];
  loading: boolean;
  open: boolean;
}

const AssignPPOSelect = (props: AssignPPOSelectProps) => {
  const {
    value,
    primaryProcedureOwnerName,
    handleSubmitPPO,
    initDisabled,
    hoverMessage,
    procedureTaskId,
    setProcedureListData,
    procedureListData,
    ...rest
  } = props;

  const assignPPOContainerRef = useRef<HTMLDivElement>(null);
  const [saveState, setSaveState] = useSetState<AssignPPOSelectState>({
    disabled: initDisabled,
    selectValue: value,
    options: [],
    loading: false,
    open: false,
  });

  const longestItem = useMemo(
    () =>
      saveState.options.reduce(
        (prev, curr) =>
          String(prev.label || "").trim().length > String(curr.label || "").trim().length
            ? prev
            : curr,
        { label: "", value: "" }
      ),
    [saveState.options]
  );

  useEffect(() => {
    setSaveState({
      selectValue: value,
    });
  }, [props.value]);

  useEffect(() => {
    setSaveState({
      disabled: initDisabled,
    });
  }, [props.initDisabled]);

  useClickAway(() => {
    setSaveState({
      ...saveState,
      open: false,
      ...(!submitLoading && { selectValue: "" }),
    });
  }, [assignPPOContainerRef, ...document.querySelectorAll(".ant5-select-dropdown")]);

  const { run: runGetOptions, loading: fetchLoading } = useRequest(
    wrapRequestToPromise(ProcedureDetailService.getPrimaryProcedureOwnerDDL),
    {
      manual: true,
      onSuccess: (result) => {
        setSaveState({
          options: result
            .map((item) => ({
              label: item.name,
              value: item.id,
            }))
            .sort(sortListV5),
          open: true,
        });
      },
      onError: () => {
        message.error("Get Primary Procedure Owner Failed");
      },
    }
  );

  const { run: runHandleSubmitPPO, loading: submitLoading } = useRequest(
    wrapRequestToPromise(handleSubmitPPO),
    {
      manual: true,
      onBefore: () => {
        setSaveState({
          loading: true,
        });
      },
      onSuccess: (res) => {
        if (res !== AssignPPOSelectStatus.Conflict) {
          const copyList = [...procedureListData];
          const targetRow = copyList.find((item) => item.procedureTaskId === procedureTaskId);
          if (!targetRow) return;
          targetRow.procedureId = res.data.procedureID;
          targetRow.primaryProcedureOwner = res.data.primaryProcedureOwnerID;
          targetRow.primaryProcedureOwnerName =
            (saveState.options.find((item) => item.value === res.data.primaryProcedureOwnerID)
              ?.label as string) || "";
          targetRow.ppoMessage = 2;
          setProcedureListData(copyList);
        }
        setSaveState({
          disabled: true,
        });
      },
      onError: (msg) => {
        message.error(msg as unknown as string);
      },
      onFinally: () => {
        setSaveState({ loading: false });
      },
    }
  );

  if (saveState.disabled) {
    return (
      <Tooltip
        placement="topLeft"
        overlayClassName="max-w-[350px]"
        title={hoverMessage ? HoverMessageType[hoverMessage] : null}
      >
        {/* have to add a Fragment element due to ref*/}
        <Fragment>
          <ReadOnlyText
            value={primaryProcedureOwnerName}
            width="10.5rem"
            className="!py-0 !h-[28px] !leading-6 !cursor-auto"
          />
        </Fragment>
      </Tooltip>
    );
  }

  const onOpenChange = (open: boolean) => {
    if (!open) return;

    runGetOptions(procedureTaskId);
  };

  return (
    <ConfigProvider5
      theme={{
        components: {
          Select: {
            borderRadius: 2,
            hoverBorderColor: "#40a9ff",
            activeBorderColor: "#40a9ff",
            activeOutlineColor: "#1890ff33",
            optionActiveBg: "#f5f5f5",
            optionSelectedBg: "#e6f7ff",
            colorText: "rgba(0,0,0,0.85)",
            optionSelectedColor: "rgba(0,0,0,0.85)",
          },
        },
      }}
    >
      <div className="assignPPOContainer" ref={assignPPOContainerRef}>
        <AntdV5Select
          {...rest}
          style={{
            width: "10.5rem",
            height: 28,
            ...props.style,
          }}
          styles={{
            popup: { root: { width: "auto", minWidth: "10.5rem", maxWidth: "18rem" } },
          }}
          disabled={saveState.loading}
          value={saveState.selectValue}
          open={saveState.open}
          options={saveState.options}
          filterOption={(input, option) => handleFilterOptionV5(input, option)}
          loading={saveState.loading || fetchLoading}
          onOpenChange={onOpenChange}
          onChange={(selectValue, option) => {
            setSaveState({
              selectValue,
              options: [
                option as AssignPPOSelectState["options"][number],
                ...saveState.options.filter((item) => item.value !== selectValue).sort(sortListV5),
              ],
            });
            const selectOptionContainers =
              document.getElementsByClassName("rc-virtual-list-holder");
            Array.from(selectOptionContainers).forEach((item) => (item.scrollTop = 0));
          }}
          popupRender={(menu) => (
            <div
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
            >
              {/* hidden element to expand width */}
              <div className="whitespace-nowrap invisible h-0 overflow-hidden px-3">
                <div className="optionItem">
                  <span className="whitespace-nowrap">{longestItem.label}</span>
                  <CheckOutlined className="checkIcon" />
                </div>
              </div>
              {menu}
              <Divider
                style={{
                  margin: "8px 0",
                }}
              />
              <div className="selectWithButtonButtonDiv">
                <Buttons
                  testid="assign-ppo-cancel-btn"
                  color="grey"
                  size="small"
                  onClick={() => {
                    setSaveState({ open: false, selectValue: "" });
                  }}
                >
                  Cancel
                </Buttons>
                <Buttons
                  testid="assign-ppo-confirm-btn"
                  color="blue"
                  disabled={saveState.selectValue.trim() === ""}
                  size="small"
                  className="confirmBtn"
                  onClick={() => {
                    setSaveState({ open: false });
                    runHandleSubmitPPO({
                      primaryProcedureOwnerId: saveState.selectValue,
                      procedureTaskId: procedureTaskId,
                    });
                  }}
                >
                  Confirm
                </Buttons>
              </div>
            </div>
          )}
          optionRender={(item) => (
            <div className="optionItem">
              <span className="whitespace-pre-wrap">{item.label}</span>
              {saveState.selectValue === item.value && <CheckOutlined className="checkIcon" />}
            </div>
          )}
        />
      </div>
    </ConfigProvider5>
  );
};

export { AssignPPOSelect, AssignPPOSelectStatus };
